import { ChevronDown, Clock, Plus } from "lucide-react";
import { DAY_LABELS } from "../../hooks/useStickyBulletin";

interface VolWatchEntry {
  time: string;
  dayOfWeek: number;
  instrument: string;
  notes: string;
}

interface HotTimeEntry {
  bucket: string;
  dayOfWeek?: number;
  count: number;
  instruments: string[];
}

export function BulletinVolWatchTab({
  entries,
  time,
  day,
  instrument,
  notes,
  isQuickVisible,
  isQuickActive,
  isHotTimesOpen,
  isHotTimesLoaded,
  isHotTimesByDay,
  hotTimes,
  selectedSymbol,
  onTimeChange,
  onDayChange,
  onInstrumentChange,
  onNotesChange,
  onToggleQuick,
  onQuickClock,
  onToggleHotTimes,
  onToggleHotTimesByDay,
  onAdd,
}: {
  entries: VolWatchEntry[];
  time: string;
  day: number;
  instrument: string;
  notes: string;
  isQuickVisible: boolean;
  isQuickActive: boolean;
  isHotTimesOpen: boolean;
  isHotTimesLoaded: boolean;
  isHotTimesByDay: boolean;
  hotTimes: HotTimeEntry[];
  selectedSymbol: string;
  onTimeChange: (value: string) => void;
  onDayChange: (value: number) => void;
  onInstrumentChange: (value: string) => void;
  onNotesChange: (value: string) => void;
  onToggleQuick: () => void;
  onQuickClock: () => void;
  onToggleHotTimes: () => void;
  onToggleHotTimesByDay: () => void;
  onAdd: () => void;
}) {
  return (
    <div className="space-y-4 animate-in fade-in duration-150">
      <div className="flex items-start justify-between gap-4">
        <p className="max-w-[300px] text-[11px] leading-relaxed text-[var(--fintheon-muted)]">
          Record NQ volatility windows and the Treasury barometer mix that
          accompanied them.
        </p>
        <button
          type="button"
          onClick={onToggleQuick}
          className="shrink-0 text-[9px] text-[var(--fintheon-accent)] transition-opacity hover:opacity-75"
        >
          {isQuickVisible ? "Hide quick" : "Show quick"}
        </button>
      </div>

      {isQuickVisible ? (
        <button
          type="button"
          onClick={onQuickClock}
          className="fintheon-black-liquid-glass fintheon-primary-action flex w-full items-center justify-center gap-2.5 rounded-xl py-3 text-[11px] font-medium text-[var(--fintheon-text)] transition-transform active:scale-[0.985]"
        >
          <Clock className={`h-4 w-4 ${isQuickActive ? "scale-110" : ""}`} />
          <span>Clock VolWatch now</span>
          <span className="font-mono text-[9px] text-[var(--fintheon-muted)]">
            {selectedSymbol || "NQ"} / {DAY_LABELS[new Date().getDay()]}
          </span>
        </button>
      ) : null}

      <div>
        <button
          type="button"
          onClick={onToggleHotTimes}
          className="flex w-full items-center justify-between py-1.5 text-[10px] uppercase tracking-[0.12em] text-[var(--fintheon-accent)] transition-opacity hover:opacity-75"
          aria-expanded={isHotTimesOpen}
        >
          <span>Observed windows</span>
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${isHotTimesOpen ? "rotate-180" : ""}`}
          />
        </button>
        {isHotTimesOpen ? (
          <HotTimes
            isLoaded={isHotTimesLoaded}
            isByDay={isHotTimesByDay}
            entries={hotTimes}
            onToggleByDay={onToggleHotTimesByDay}
          />
        ) : null}
      </div>

      <div className="space-y-2.5">
        <p className="text-[9px] uppercase tracking-[0.14em] text-[var(--fintheon-muted)]">
          Manual observation
        </p>
        <div className="grid grid-cols-[1fr_0.8fr] gap-3">
          <Field label="Time">
            <input
              type="time"
              value={time}
              onChange={(event) => onTimeChange(event.target.value)}
            />
          </Field>
          <Field label="Day">
            <select
              value={day}
              onChange={(event) => onDayChange(Number(event.target.value))}
            >
              {DAY_LABELS.map((label, index) => (
                <option key={label} value={index}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-[0.7fr_1.3fr] gap-3">
          <Field label="Instrument">
            <select
              value={instrument}
              onChange={(event) => onInstrumentChange(event.target.value)}
            >
              {["ES", "NQ", "YM", "RTY", "CL", "GC", "SI", "BTC"].map(
                (symbol) => (
                  <option key={symbol}>{symbol}</option>
                ),
              )}
            </select>
          </Field>
          <Field label="Note">
            <input
              value={notes}
              onChange={(event) => onNotesChange(event.target.value)}
              placeholder="Optional"
            />
          </Field>
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="flex items-center gap-1.5 py-1 text-[10px] font-medium uppercase tracking-wide text-[var(--fintheon-accent)] transition-opacity hover:opacity-75"
        >
          <Plus className="h-3 w-3" /> Log VolWatch
        </button>
      </div>

      {entries.length > 0 ? <RecentEntries entries={entries} /> : null}
    </div>
  );
}

function HotTimes({
  isLoaded,
  isByDay,
  entries,
  onToggleByDay,
}: {
  isLoaded: boolean;
  isByDay: boolean;
  entries: HotTimeEntry[];
  onToggleByDay: () => void;
}) {
  return (
    <div className="mt-1 space-y-2 py-2 animate-in fade-in slide-in-from-top-1 duration-150">
      <div className="flex items-center justify-between text-[9px] text-[var(--fintheon-muted)]">
        <span>Top three fifteen-minute windows</span>
        <button
          type="button"
          onClick={onToggleByDay}
          className="text-[var(--fintheon-accent)]"
        >
          {isByDay ? "By day" : "All days"}
        </button>
      </div>
      {!isLoaded ? <StateText>Loading observations...</StateText> : null}
      {isLoaded && entries.length === 0 ? (
        <StateText>No VolWatch observations yet.</StateText>
      ) : null}
      {entries.map((entry) => (
        <div
          key={`${entry.bucket}:${entry.dayOfWeek ?? "all"}`}
          className="grid grid-cols-[1fr_auto_auto] items-center gap-3 py-1 font-mono text-[10px]"
        >
          <span className="text-[var(--fintheon-text)]">{entry.bucket}</span>
          <span className="text-[var(--fintheon-muted)]">
            {entry.dayOfWeek == null
              ? entry.instruments.join(", ")
              : DAY_LABELS[entry.dayOfWeek]}
          </span>
          <span className="tabular-nums text-[var(--fintheon-accent)]">
            {entry.count}
          </span>
        </div>
      ))}
    </div>
  );
}

function RecentEntries({ entries }: { entries: VolWatchEntry[] }) {
  return (
    <div className="space-y-1">
      <p className="text-[9px] uppercase tracking-[0.14em] text-[var(--fintheon-muted)]">
        Recent observations
      </p>
      {entries.slice(0, 6).map((entry, index) => (
        <div
          key={`${entry.time}:${entry.dayOfWeek}:${index}`}
          className="grid grid-cols-[auto_auto_auto_1fr] gap-2 py-1.5 font-mono text-[10px]"
        >
          <span className="tabular-nums text-[var(--fintheon-text)]">
            {entry.time}
          </span>
          <span className="text-[var(--fintheon-muted)]">
            {DAY_LABELS[entry.dayOfWeek]}
          </span>
          <span className="text-[var(--fintheon-accent)]">
            {entry.instrument}
          </span>
          <span className="truncate text-[var(--fintheon-muted)]">
            {entry.notes}
          </span>
        </div>
      ))}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="grid gap-1 border-b border-[var(--fintheon-accent)]/14 pb-1 text-[8px] uppercase tracking-[0.12em] text-[var(--fintheon-muted)] [&_input]:min-w-0 [&_input]:bg-transparent [&_input]:text-[11px] [&_input]:normal-case [&_input]:text-[var(--fintheon-text)] [&_input]:outline-none [&_select]:min-w-0 [&_select]:bg-transparent [&_select]:text-[11px] [&_select]:normal-case [&_select]:text-[var(--fintheon-text)] [&_select]:outline-none">
      {label}
      {children}
    </label>
  );
}

function StateText({ children }: { children: React.ReactNode }) {
  return (
    <p className="py-2 text-center text-[10px] text-[var(--fintheon-muted)]">
      {children}
    </p>
  );
}
